import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useMessages } from '@/hooks/useMessages';

const navItems = [
  { label: '首页', path: '/' },
  { label: '绿证出售信息', path: '/supply' },
  { label: '绿证采购需求', path: '/demand' },
  // { label: '行业资讯', path: '/news' }, {/* 行业资讯暂不上线 */}
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { totalUnread, loadConversations } = useMessages();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  // Refresh unread badge on login and on each route change.
  useEffect(() => {
    if (isAuthenticated) loadConversations();
  }, [isAuthenticated, location, loadConversations]);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  return (
    <>
      <div className="w-full bg-accent-500 text-background-50 text-xs py-1.5 text-center font-label">
        <span className="opacity-90">
          风险提示：本平台为免费信息撮合平台，不参与交易、资金结算及权属过户，信息真实性由发布方负责，请自行甄别风险。
        </span>
      </div>

      <nav
        className={`w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'fixed top-0 left-0 bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'relative bg-transparent'
        }`}
        style={scrolled ? { marginTop: '0' } : {}}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-leaf-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-foreground-900 text-lg font-semibold tracking-tight">
                绿证通
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    location.pathname === item.path
                      ? 'text-primary-600'
                      : 'text-foreground-700 hover:text-primary-600'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-3-line text-primary-600 text-xs"></i>
                    </div>
                    <span className="max-w-[100px] truncate">{user.companyName || user.contactName}</span>
                    {userMenuOpen ? (
                      <i className="ri-arrow-up-s-line text-xs"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-xs"></i>
                    )}
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-background-50 rounded-lg border border-background-200 shadow-lg py-1 z-50">
                      <div className="px-3 py-2 border-b border-background-100">
                        <p className="text-foreground-900 text-sm font-medium truncate">{user.companyName || user.contactName}</p>
                        <p className="text-foreground-400 text-xs mt-0.5">
                          {user.accountType === 'company' ? '企业账户' : '个人账户'}
                        </p>
                      </div>
                      <Link
                        to="/profile"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-user-settings-line"></i>
                        个人中心
                      </Link>
                      <Link
                        to="/messages"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-chat-3-line"></i>
                        站内信
                        {totalUnread > 0 && (
                          <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 text-[10px] font-medium flex items-center justify-center">
                            {totalUnread > 99 ? '99+' : totalUnread}
                          </span>
                        )}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer text-left"
                      >
                        <i className="ri-logout-box-r-line"></i>
                        退出登录
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    登录
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 border border-primary-500 text-primary-600 text-sm font-medium rounded-md hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    注册
                  </Link>
                </>
              )}
            </div>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70">
            <div className="px-4 py-4 flex flex-col gap-3">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium py-2 cursor-pointer ${
                    location.pathname === item.path
                      ? 'text-primary-600'
                      : 'text-foreground-700'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-background-200/70 pt-3 flex flex-col gap-2">
                {isAuthenticated && user ? (
                  <>
                    <div className="px-3 py-2 text-sm text-foreground-700 bg-background-100 rounded-md">
                      <span className="font-medium">{user.companyName || user.contactName}</span>
                      <span className="text-foreground-400 ml-2 text-xs">
                        ({user.accountType === 'company' ? '企业账户' : '个人账户'})
                      </span>
                    </div>
                    <Link
                      to="/profile"
                      className="py-2 text-center text-sm font-medium text-foreground-700 border border-background-300 rounded-md cursor-pointer"
                    >
                      个人中心
                    </Link>
                    <Link
                      to="/messages"
                      className="py-2 text-center text-sm font-medium text-foreground-700 border border-background-300 rounded-md cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      站内信
                      {totalUnread > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 text-[10px] font-medium flex items-center justify-center">
                          {totalUnread > 99 ? '99+' : totalUnread}
                        </span>
                      )}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="py-2 text-center text-sm font-medium text-foreground-600 border border-background-300 rounded-md cursor-pointer"
                    >
                      退出登录
                    </button>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Link
                      to="/login"
                      className="flex-1 py-2 text-center text-sm font-medium text-foreground-700 border border-background-300 rounded-md cursor-pointer"
                    >
                      登录
                    </Link>
                    <Link
                      to="/register"
                      className="flex-1 py-2 text-center text-sm font-medium text-primary-600 border border-primary-500 rounded-md cursor-pointer"
                    >
                      注册
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}